import React from 'react';
import { motion } from 'framer-motion';

const LateNightShoppingLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Late Night Shopping Guide: Why You Buy More After Dark
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Track Your Midnight Impulse Buys</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Late-night shoppers spend 30% more and experience 42% higher rates of purchase regret. Understand your nighttime shopping patterns and break the cycle.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Late Night Purchase
            </motion.button>
          </div>
          <div className="text-8xl">🌙</div>
        </div>
      </div>
      
      {/* Late Night Shopping Problem */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">The Late Night Shopping Problem</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧠</span>
              <div>
                <h3 className="font-bold dark:text-white">Depleted Willpower</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Your brain's decision-making centers are fatigued at night, making you more susceptible to impulse buys.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😴</span>
              <div>
                <h3 className="font-bold dark:text-white">Tired Decision Making</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Fatigue impairs judgment, causing you to focus on benefits and ignore downsides of purchases.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📱</span>
              <div>
                <h3 className="font-bold dark:text-white">Bedtime Scrolling</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The habit of browsing shopping apps in bed leads to sleepy purchases you wouldn't make while alert.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😌</span>
              <div>
                <h3 className="font-bold dark:text-white">Comfort Seeking</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  At night, we often shop for emotional comfort rather than practical need, leading to regrettable purchases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Late Night Shopping Stats */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Nighttime Shopping Reality Check</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">10-11PM</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">peak hour for impulse purchases</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">30%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">higher average cart value after 9PM</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">67%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of bedtime shopping occurs on smartphones</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">42%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">higher return rates on night purchases</p>
          </div>
        </div>
      </section>
      
      {/* How Our Tool Helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Late Night Shopping Guide Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Time-of-Day Purchase Tracking
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              See patterns in when you shop and how time affects your spending habits and purchase satisfaction.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Nighttime Shopping Protection
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Set up customized safeguards like cart holding periods that activate after your personal "decision fatigue" hour.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Morning-After Purchase Review
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Implement a system to review night purchases in the morning when you're refreshed and thinking clearly.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Digital Sunset Reminders
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get personalized suggestions for alternative evening activities to replace the bedtime shopping habit.
            </p>
          </div>
        </div>
      </section>
      
      {/* Nighttime Shopper Types */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Nighttime Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛌</span>
              <h3 className="font-bold mt-2 dark:text-white">The Bedtime Browser</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You shop to wind down before sleep, scrolling through products as a nightly ritual that often ends in purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🌃</span>
              <h3 className="font-bold mt-2 dark:text-white">The Midnight Splurger</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Your biggest purchases happen late at night when fatigue lowers your financial inhibitions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">The Evening Overthinker</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You research products all day but make final decisions at night when decision fatigue pushes you to complete the purchase.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">😴</span>
              <h3 className="font-bold mt-2 dark:text-white">The Sleepy Self-Gifter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You buy things at night as a form of self-comfort when you're tired, lonely, or seeking a quick mood boost.
            </p>
          </div>
        </div>
      </section>
      
      {/* Circadian Shopping Graph */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">The Circadian Shopping Cycle</h2>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium dark:text-white">Morning (6am-12pm)</span>
                <span className="font-medium dark:text-white">Rational Purchases</span>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: "65%" }}></div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Clear-headed decisions, lower spending, practical items
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium dark:text-white">Afternoon (12pm-6pm)</span>
                <span className="font-medium dark:text-white">Mixed Decision Quality</span>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-500 rounded-full" style={{ width: "50%" }}></div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Moderate willpower, some impulse purchases
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium dark:text-white">Evening (6pm-10pm)</span>
                <span className="font-medium dark:text-white">Increasing Impulsivity</span>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full" style={{ width: "70%" }}></div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Decision fatigue setting in, higher spending
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium dark:text-white">Late Night (10pm-2am)</span>
                <span className="font-medium dark:text-white">Peak Impulse Zone</span>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full" style={{ width: "90%" }}></div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Highest cart values, most regretted purchases
              </div>
            </div>
          </div>
          
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
            Decision quality and spending patterns throughout a typical 24-hour cycle
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Tamed Their Nighttime Shopping</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I didn't realize how much I was spending during my nightly phone scrolling until I tracked it. Switching to morning-only purchases has saved me $200 monthly on things I don't actually need."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Ethan K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Bedtime Browser</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "The 'morning review' feature changed everything. Now I fill carts at night but wait until morning to check out. Half the time I realize I don't even want what I selected when I was tired!"
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Nia W.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Midnight Splurger</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Late Night Shopping</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I shop more at night?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Your brain experiences decision fatigue after a full day of choices, making you more susceptible to impulse buys. Evening also brings lower willpower, increased desire for comfort, and the relaxing but dangerous habit of browsing in bed when your rational decision-making is compromised.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I stop late-night impulse buying?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Implement a "digital sunset" by removing shopping apps from your bedtime routine, use the 24-hour rule (fill carts but purchase the next day), set up app blockers after certain hours, or create a dedicated "shopping hour" earlier in the day when your judgment is sharper.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Does time of day really affect buying decisions?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes! Research shows that shoppers spend 30% more in evening hours and are significantly more likely to make unplanned purchases after 9PM. Circadian rhythms affect decision-making abilities, with willpower and rational thinking naturally declining as the day progresses.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What can I do instead of nighttime shopping?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Replace the habit with relaxing alternatives like reading, journaling, or meditation. If you still want to browse products, use the "wishlist" feature instead of the cart, or dedicate a notebook to writing down items you want to consider purchasing—then review them the next morning.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Tame Your Nighttime Shopping?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your late-night purchases and discover how time of day affects your spending habits.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Night Shopping Guilt
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Late Night Shopping</h2>
          <p className="mb-3">
            Late night shopping refers to the common habit of making online purchases during evening hours, particularly right before bed. This behavior is influenced by decision fatigue, reduced willpower, and the comfort-seeking tendencies that naturally occur as we become tired.
          </p>
          <p className="mb-3">
            Our Late Night Shopping Guide helps you understand how your circadian rhythms affect spending decisions and provides tools to protect yourself during vulnerable hours when judgment is compromised by fatigue.
          </p>
          <p>
            Whether you're dealing with bedtime browsing habits, midnight impulse buys, or a pattern of making your biggest purchases when you're most tired, our free tool helps you develop healthier shopping patterns aligned with your body's natural decision-making cycles.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default LateNightShoppingLanding;
